/**
 * RemoveAccountSection Component
 * Permanently delete account with password confirmation
 */

import { Eye, EyeOff } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import { useDeleteAccount } from "../hooks/useSettings"

export function RemoveAccountSection() {
  const { t } = useTranslation("settings")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const { mutate: deleteAccount, isPending } = useDeleteAccount()

  const handleRemoveAccount = (e: React.FormEvent) => {
    e.preventDefault()
    if (!password.trim()) {
      toast.error(t("removeAccount.validationRequired"))
      return
    }
    deleteAccount(password)
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Section Header */}
      <div>
        <h1 className="text-2xl font-bold text-charcoal uppercase section-heading">
          {t("removeAccount.title")}
        </h1>
        <p className="text-sm text-stone mt-1">{t("removeAccount.description")}</p>
      </div>

      <div>
        <p>{t("removeAccount.descriptionConfirm")}</p>
      </div>

      {/* Confirmation Form */}
      <form onSubmit={handleRemoveAccount} className="flex flex-col gap-6 max-w-md">
        <div className="space-y-2">
          <Label htmlFor="remove-account-password" className="text-xs uppercase text-stone">
            {t("removeAccount.password")}
          </Label>
          <div className="relative">
            <Input
              id="remove-account-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t("removeAccount.confirmPassword")}
              className={cn(
                "border-0 border-b rounded-none shadow-none px-0 text-sm font-semibold focus-visible:ring-0 pr-10",
              )}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-ash hover:text-stone cursor-pointer"
              aria-label={
                showPassword ? t("removeAccount.hidePassword") : t("removeAccount.showPassword")
              }
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            variant="primary"
            className="bg-brand-terracotta px-10 text-white"
            size="md"
            isLoading={isPending}
          >
            {t("removeAccount.remove")}
          </Button>
        </div>
      </form>
    </div>
  )
}
